'use client';
import React, { useMemo } from 'react';
import { ArrowLeft, Trophy, Target, Clock, TrendingUp, BookOpen, Zap, Award } from 'lucide-react';
import { QuizSession } from './QuizArenaContent';

interface QuizStatsProps {
  history: QuizSession[];
  onBack: () => void;
}

export default function QuizStats({ history, onBack }: QuizStatsProps) {
  const stats = useMemo(() => {
    if (history.length === 0) return null;

    const totalQuizzes = history.length;
    const totalScore = history.reduce((s, h) => s + h.totalScore, 0);
    const totalCorrect = history.reduce((s, h) => s + h.attempts.filter(a => a.isCorrect).length, 0);
    const totalAttempts = history.reduce((s, h) => s + h.attempts.length, 0);
    const totalTime = history.reduce((s, h) => s + h.totalTime, 0);
    const avgAccuracy = Math.round((totalCorrect / totalAttempts) * 100);
    const avgScore = Math.round(totalScore / totalQuizzes);
    const avgTime = totalAttempts > 0 ? Math.round(totalTime / totalAttempts) : 0;
    const bestScore = Math.max(...history.map(h => h.totalScore));
    const bestAccuracy = Math.max(...history.map(h => {
      const c = h.attempts.filter(a => a.isCorrect).length;
      return Math.round((c / h.attempts.length) * 100);
    }));

    // Category breakdown
    const categoryMap: Record<string, { correct: number; total: number }> = {};
    history.forEach(h => {
      const cat = h.category;
      if (!categoryMap[cat]) categoryMap[cat] = { correct: 0, total: 0 };
      categoryMap[cat].correct += h.attempts.filter(a => a.isCorrect).length;
      categoryMap[cat].total += h.attempts.length;
    });

    const categories = Object.entries(categoryMap).map(([name, data]) => ({
      name,
      accuracy: Math.round((data.correct / data.total) * 100),
      total: data.total,
    })).sort((a, b) => b.accuracy - a.accuracy);

    // Score trend (last 10)
    const trend = history.slice(0, 10).reverse().map((h, i) => ({
      index: i + 1,
      score: h.totalScore,
      accuracy: Math.round((h.attempts.filter(a => a.isCorrect).length / h.attempts.length) * 100),
      title: h.title,
    }));

    return { totalQuizzes, totalScore, totalCorrect, totalAttempts, avgAccuracy, avgScore, avgTime, bestScore, bestAccuracy, categories, trend };
  }, [history]);

  if (!stats) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={onBack} className="flex items-center gap-2 text-sm mb-6 hover:opacity-70 transition-opacity"
          style={{ color: 'var(--muted-foreground)' }}>
          <ArrowLeft size={15} /> Back to Lobby
        </button>
        <div className="rounded-2xl p-12 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div className="text-5xl mb-4">📊</div>
          <h2 className="text-xl font-bold text-foreground mb-2">No Stats Yet</h2>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Complete a quiz to see your statistics here.</p>
        </div>
      </div>
    );
  }

  const maxTrendScore = Math.max(...stats.trend.map(t => t.score), 1);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <button onClick={onBack} className="flex items-center gap-2 text-sm mb-6 hover:opacity-70 transition-opacity"
        style={{ color: 'var(--muted-foreground)' }}>
        <ArrowLeft size={15} /> Back to Lobby
      </button>

      <h1 className="text-2xl font-extrabold text-foreground mb-6">My Quiz Statistics</h1>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Quizzes Played', value: stats.totalQuizzes, icon: BookOpen, color: '#7C3AED' },
          { label: 'Total Score', value: stats.totalScore.toLocaleString(), icon: Zap, color: '#F59E0B' },
          { label: 'Avg Accuracy', value: `${stats.avgAccuracy}%`, icon: Target, color: '#10B981' },
          { label: 'Avg Time/Q', value: `${stats.avgTime}s`, icon: Clock, color: '#06B6D4' },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2 mb-2">
              <kpi.icon size={14} style={{ color: kpi.color }} />
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{kpi.label}</span>
            </div>
            <p className="text-xl font-extrabold text-foreground">{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Personal Bests */}
      <div className="rounded-2xl p-5 mb-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
          <Award size={16} style={{ color: '#F59E0B' }} /> Personal Bests
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl p-4" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
            <p className="text-xs mb-1" style={{ color: '#F59E0B' }}>Best Score</p>
            <p className="text-2xl font-extrabold" style={{ color: '#F59E0B' }}>{stats.bestScore}</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>points in one quiz</p>
          </div>
          <div className="rounded-xl p-4" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
            <p className="text-xs mb-1" style={{ color: '#10B981' }}>Best Accuracy</p>
            <p className="text-2xl font-extrabold" style={{ color: '#10B981' }}>{stats.bestAccuracy}%</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>correct answers</p>
          </div>
        </div>
      </div>

      {/* Score Trend */}
      {stats.trend.length > 1 && (
        <div className="rounded-2xl p-5 mb-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
            <TrendingUp size={16} style={{ color: '#06B6D4' }} /> Score Trend
          </h3>
          <div className="flex items-end gap-2 h-24">
            {stats.trend.map((t, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-t-md transition-all duration-500"
                  style={{
                    height: `${Math.max(8, (t.score / maxTrendScore) * 80)}px`,
                    background: `linear-gradient(180deg, #7C3AED, #06B6D4)`,
                    opacity: 0.7 + (i / stats.trend.length) * 0.3,
                  }} />
                <span className="text-xs font-bold" style={{ color: 'var(--muted-foreground)', fontSize: '10px' }}>
                  {t.score}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs mt-2 text-center" style={{ color: 'var(--muted-foreground)' }}>Last {stats.trend.length} quizzes</p>
        </div>
      )}

      {/* Category Performance */}
      {stats.categories.length > 0 && (
        <div className="rounded-2xl p-5 mb-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
            <Trophy size={16} style={{ color: '#A78BFA' }} /> Category Performance
          </h3>
          <div className="space-y-3">
            {stats.categories.map((cat) => (
              <div key={cat.name}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">{cat.name}</span>
                  <span className="text-sm font-bold"
                    style={{ color: cat.accuracy >= 70 ? '#10B981' : cat.accuracy >= 40 ? '#F59E0B' : '#F43F5E' }}>
                    {cat.accuracy}%
                  </span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${cat.accuracy}%`,
                      background: cat.accuracy >= 70 ? '#10B981' : cat.accuracy >= 40 ? '#F59E0B' : '#F43F5E',
                    }} />
                </div>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{cat.total} questions attempted</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History Table */}
      <div className="rounded-2xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <h3 className="font-bold text-foreground mb-4">Quiz History</h3>
        <div className="space-y-2">
          {history.map((session, i) => {
            const correct = session.attempts.filter(a => a.isCorrect).length;
            const accuracy = Math.round((correct / session.attempts.length) * 100);
            return (
              <div key={i} className="flex items-center justify-between py-2.5 px-3 rounded-xl"
                style={{ background: 'var(--muted)' }}>
                <div>
                  <p className="text-sm font-semibold text-foreground">{session.title}</p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    {correct}/{session.attempts.length} correct · {new Date(session.completedAt).toLocaleDateString()} · {Math.round(session.totalTime / 60)}m {session.totalTime % 60}s
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold" style={{ color: '#F59E0B' }}>{session.totalScore} pts</p>
                  <p className="text-xs font-semibold"
                    style={{ color: accuracy >= 70 ? '#10B981' : accuracy >= 40 ? '#F59E0B' : '#F43F5E' }}>
                    {accuracy}%
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
